/**
 * CampaignInjectionQueue — Admin → Viral
 * ------------------------------------------------------------------
 * Tactical bench for attaching recently-generated assets into the
 * active campaign chain — assign strategic role, mark as
 * reinforcement asset, mark as escalation pivot.
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Crosshair, Plus, ArrowUp, ArrowDown, Flame } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  CAMPAIGN_CREATED_EVENT, CAMPAIGN_UPDATED_EVENT,
  fetchCampaigns, fetchCampaignSequences, attachAssetToCampaign,
  reorderSequence, setSequenceFlags, ROLE_LABEL, ROLE_SEQUENCE, type StrategicRole,
} from "@/lib/campaignEngine";

export default function CampaignInjectionQueue() {
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [seqs, setSeqs] = useState<any[]>([]);
  const [recentAssets, setRecentAssets] = useState<any[]>([]);
  const [pickAsset, setPickAsset] = useState<string>("");
  const [pickRole, setPickRole] = useState<StrategicRole>("behavioral_reveal");

  const loadCamps = async () => {
    const c = await fetchCampaigns();
    setCampaigns(c);
    if (!activeId && c[0]) setActiveId(c[0].id);
  };

  const loadSeqs = async (id: string) => {
    if (!id) { setSeqs([]); return; }
    setSeqs(await fetchCampaignSequences(id));
  };

  const loadAssets = async () => {
    const { data } = await (supabase as any)
      .from("content_assets")
      .select("id, scenario_title, archetype, format, platform, created_at")
      .order("created_at", { ascending: false })
      .limit(40);
    setRecentAssets(data ?? []);
  };

  useEffect(() => { loadCamps(); loadAssets(); }, []);
  useEffect(() => { loadSeqs(activeId); }, [activeId]);

  useEffect(() => {
    const refresh = () => { loadCamps(); loadSeqs(activeId); };
    window.addEventListener(CAMPAIGN_CREATED_EVENT, refresh);
    window.addEventListener(CAMPAIGN_UPDATED_EVENT, refresh);
    return () => {
      window.removeEventListener(CAMPAIGN_CREATED_EVENT, refresh);
      window.removeEventListener(CAMPAIGN_UPDATED_EVENT, refresh);
    };
  }, [activeId]);

  const inject = async () => {
    if (!activeId || !pickAsset) return;
    try {
      await attachAssetToCampaign({
        campaignId: activeId,
        assetId: pickAsset,
        role: pickRole,
        isReinforcement: pickRole === "replay_reinforcement",
        isPivot: pickRole === "escalation_confrontation",
      });
      toast({ title: "Asset injected", description: ROLE_LABEL[pickRole] });
      setPickAsset("");
      loadSeqs(activeId);
    } catch (e: any) {
      toast({ title: "Inject failed", description: e.message, variant: "destructive" });
    }
  };

  const move = async (seq: any, dir: -1 | 1) => {
    const target = seqs.find((s) => s.sequence_order === seq.sequence_order + dir);
    if (target) {
      await reorderSequence(seq.id, target.sequence_order);
      await reorderSequence(target.id, seq.sequence_order);
    } else {
      await reorderSequence(seq.id, seq.sequence_order + dir);
    }
    loadSeqs(activeId);
  };

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Crosshair className="w-5 h-5 text-cyan" />
          Campaign Injection Queue
          <Badge variant="outline" className="text-[10px] border-cyan/40 text-cyan ml-2">
            {seqs.length} chained
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {campaigns.length === 0 && (
          <div className="text-xs text-muted-foreground italic">No active campaigns. Deploy one in Strategy → Builder first.</div>
        )}

        {campaigns.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <Select value={activeId} onValueChange={setActiveId}>
                <SelectTrigger className="bg-charcoal-deep border-glass-border h-9 text-xs">
                  <SelectValue placeholder="Select campaign" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.campaign_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={pickAsset} onValueChange={setPickAsset}>
                <SelectTrigger className="bg-charcoal-deep border-glass-border h-9 text-xs">
                  <SelectValue placeholder="Pick asset to inject" />
                </SelectTrigger>
                <SelectContent>
                  {recentAssets.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="font-mono text-[10px] mr-2 text-muted-foreground">{a.archetype}</span>
                      {a.scenario_title?.slice(0, 36) ?? a.id.slice(0, 8)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex gap-1.5">
                <Select value={pickRole} onValueChange={(v) => setPickRole(v as StrategicRole)}>
                  <SelectTrigger className="bg-charcoal-deep border-glass-border h-9 text-xs flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_SEQUENCE.map((r) => (
                      <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={inject} disabled={!pickAsset}
                  className="h-9 bg-cyan/20 text-cyan border border-cyan/50 hover:bg-cyan/30">
                  <Plus className="w-3 h-3 mr-1" />Inject
                </Button>
              </div>
            </div>

            <div className="rounded border border-glass-border bg-charcoal-deep/60 divide-y divide-glass-border">
              {seqs.length === 0 && (
                <div className="text-xs text-muted-foreground p-3 italic">Empty chain — inject an asset to begin sequencing.</div>
              )}
              {seqs.map((s) => (
                <div key={s.id} className="p-2.5 flex items-center gap-3">
                  <div className="text-cyan font-mono text-xs w-6">#{s.sequence_order}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-foreground truncate">
                      {ROLE_LABEL[s.strategic_role as StrategicRole] ?? s.strategic_role}
                    </div>
                    <div className="text-[10px] text-muted-foreground font-mono">
                      {s.pacing_type} · ret {Number(s.projected_retention_impact).toFixed(0)}% · replay {Number(s.projected_replay_impact).toFixed(0)}%
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[10px]">
                    <label className="flex items-center gap-1 text-emerald-300">
                      <Switch
                        checked={!!s.is_reinforcement}
                        onCheckedChange={(v) => setSequenceFlags(s.id, { is_reinforcement: v }).then(() => loadSeqs(activeId))}
                        className="data-[state=checked]:bg-emerald-500 scale-75"
                      />
                      reinforce
                    </label>
                    <label className="flex items-center gap-1 text-red-400">
                      <Switch
                        checked={!!s.is_escalation_pivot}
                        onCheckedChange={(v) => setSequenceFlags(s.id, { is_escalation_pivot: v }).then(() => loadSeqs(activeId))}
                        className="data-[state=checked]:bg-red-500 scale-75"
                      />
                      <Flame className="w-2.5 h-2.5" />pivot
                    </label>
                  </div>
                  <div className="flex flex-col">
                    <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => move(s, -1)} disabled={s.sequence_order <= 1}>
                      <ArrowUp className="w-3 h-3" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => move(s, +1)}>
                      <ArrowDown className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
